import { useMemo, useState } from "react";
import Button from "../../components/Button";
import Countdown from "../../components/Countdown";
import Icon from "../../components/icons/Icon";
import JahrUebersicht from "../../components/JahrUebersicht";
import PunktePille from "../../components/PunktePille";
import SongLoesung from "../../components/SongLoesung";
import SpielerKacheln from "../../components/SpielerKacheln";
import SpielKopf from "../../components/SpielKopf";
import SpielMenue from "../../components/SpielMenue";
import StatusPille, { WartePunkte } from "../../components/StatusPille";
import Stopp from "../../components/Stopp";
import TaskHeading from "../../components/TaskHeading";
import Vinyl from "../../components/Vinyl";
import { AUFGABEN } from "../../aufgaben";
import { farbNummern } from "../../spieler";
import "./HostSpiel.css";

const KNAPP_AB = 5;

const zeitText = (sek) => `${Math.floor(sek / 60)}:${String(sek % 60).padStart(2, "0")}`;

/**
 * Host-Spielbildschirm: Kopf · Mitte · Daumenzone, je nach Rundenphase.
 * Die Logik bleibt in App.js; hier wird nur zusammengesetzt.
 *
 * Phasen: TASK (Aufgabe ansagen) → PLAYING (Runde laeuft) → SOLVED
 * (gestoppt) → JUDGE (Bewertung). READY = Song geladen, noch nicht
 * gestartet. hostBuzzer: Buzzer-Bereich des mitspielenden Hosts.
 * leisten: Hinweis-Leisten (Spotify weg, Login abgelaufen, anderes Fenster).
 */
export default function HostSpiel({
  gameMode,
  raumCode,
  roomStatus,
  roundCount,
  roundStage,
  taskType,
  timer,
  timerGesamt,
  dauerSek,
  players,
  verlaufAnzahl,
  isBuzzerHost,
  hostBuzzer,
  jahrEingabeOben,
  bereitLaeuft,
  songData,
  isSongLoading,
  jahrErgebnis,
  meineId,
  raumSpieler,
  punktGegeben,
  hatPunkt,
  menueOffen,
  setMenueOffen,
  onVergebePunkt,
  onKeiner,
  keinerGewaehlt,
  jahrAuswertungAus = false,
  onNaechsteRunde,
  onWeiterhoeren,
  onBereit,
  onStart,
  onStopp,
  onSkip,
  onNochmal,
  onAufloesen,
  setShowSolution,
  onShowHistory,
  onShowSettings,
  onShowScores,
  onShowRoom,
  onBeenden,
  leisten,
  children,
}) {
  const [halten, setHalten] = useState(false);
  const farbe = useMemo(() => farbNummern(players.map((p, i) => p.name || `Spieler ${i + 1}`)), [players]);
  const name = (p, i) => p.name || `Spieler ${i + 1}`;
  const eintraege = players.map((p, i) => ({ key: i, name: name(p, i), score: p.score }));
  const aufgabe = AUFGABEN[taskType];

  const rest = timer === null || timer === undefined || !timerGesamt ? null : timer / timerGesamt;
  const knapp = roundStage === "PLAYING" && timer !== null && timer <= KNAPP_AB;

  // Raum-Verbindung: die einzige Stelle, an der der Host davon erfaehrt
  let raumHinweis = null;
  if (gameMode === "BUZZER" && !raumCode) raumHinweis = <span className="raum-hinweis">Raum weg</span>;
  else if (gameMode === "BUZZER" && roomStatus !== "connected")
    raumHinweis = <span className="raum-hinweis">verbinde…</span>;

  const kopf = (
    <SpielKopf
      wert={roundCount}
      hinweis={raumHinweis}
      pille={<PunktePille eintraege={eintraege} farbe={farbe} onClick={onShowScores} />}
      onVerlauf={onShowHistory}
      verlaufAnzahl={verlaufAnzahl}
      onMenue={() => setMenueOffen(true)}
    />
  );

  const menue = menueOffen && (
    <SpielMenue
      onClose={() => setMenueOffen(false)}
      eintraege={[
        { icon: "einstellungen", label: "Einstellungen", onClick: () => { setMenueOffen(false); onShowSettings(); } },
        { icon: "punkte", label: "Punkte anpassen", onClick: () => { setMenueOffen(false); onShowScores(); } },
        gameMode === "BUZZER" && raumCode && {
          icon: "qr",
          label: "Code & QR zeigen",
          zusatz: raumCode,
          onClick: () => { setMenueOffen(false); onShowRoom(); },
        },
        {
          icon: "flagge",
          label: "Spiel beenden",
          gefahr: true,
          frage: "Das Spiel endet für alle. Danach kommt der Endstand.",
          bestaetigen: "Spiel beenden",
          onClick: () => { setMenueOffen(false); onBeenden(); },
        },
      ]}
    />
  );

  let mitte;
  let zone;
  let stimmung = "";

  if (roundStage === "TASK") {
    const wie = isBuzzerHost ? "buzzert am Handy" : "ruft laut – du tippst STOPP";
    mitte = (
      <main className="spiel-mitte">
        <TaskHeading
          typ={taskType}
          ueberzeile={`Runde ${roundCount} · Nächste Aufgabe`}
          text={
            // Jahres-Runde im Buzzer-Modus: kein Buzzer, alle tippen
            taskType === "YEAR" && isBuzzerHost
              ? "Alle tippen das Jahr am Handy – wer am nächsten dran ist, gewinnt."
              : `Wer ${aufgabe?.akkusativ || "es"} zuerst weiß, ${wie}.`
          }
        />
        <Vinyl rest={dauerSek ? 1 : null} groesse="mittel" />
      </main>
    );
    zone = (
      <div className="daumenzone">
        <Button variant="haupt" disabled={bereitLaeuft} onClick={onBereit}>
          <Icon name="play" />
          {bereitLaeuft ? "Song lädt…" : "Song starten"}
        </Button>
        <p className="hinweis-text">
          {dauerSek ? `${dauerSek} Sekunden pro Song` : "Ohne Zeitlimit"} · ändern im Menü
        </p>
      </div>
    );
  } else if (roundStage === "READY") {
    mitte = (
      <main className="spiel-mitte">
        <TaskHeading typ={taskType} />
        <Vinyl rest={rest ?? (dauerSek ? 1 : null)} />
      </main>
    );
    zone = (
      <div className="daumenzone">
        <Button variant="haupt" onClick={onStart}>
          <Icon name="play" />
          Musik starten
        </Button>
        <Button variant="text" onClick={onSkip}>
          <Icon name="skip" />
          Song überspringen
        </Button>
      </div>
    );
  } else if (roundStage === "PLAYING") {
    stimmung = " stimmung-rot";
    mitte = (
      <main className="spiel-mitte">
        <TaskHeading typ={taskType} />
        <Vinyl playing rest={rest} dauer={timerGesamt} knapp={knapp} groesse={jahrEingabeOben ? "klein" : "gross"} />
        <div className="deck-zeile">
          <Countdown sekunden={timer} knapp={knapp} />
          <StatusPille laeuft />
        </div>
      </main>
    );
    zone = (
      <div className="daumenzone">
        {hostBuzzer}
        <Stopp scharf unterzeile={isBuzzerHost ? "Musik anhalten" : "Jemand weiß es"} onClick={onStopp}>
          STOPP
        </Stopp>
        <Button variant="text" onClick={onSkip}>
          <Icon name="skip" />
          Song überspringen
        </Button>
      </div>
    );
  } else if (roundStage === "SOLVED") {
    mitte = (
      <main className="spiel-mitte">
        <TaskHeading
          typ={taskType}
          ueberzeile={timer !== null && timer !== undefined && timer > 0 ? `Gestoppt bei ${zeitText(timer)}` : "Gestoppt"}
        />
        <Vinyl rest={rest} dauer={timerGesamt} groesse={jahrEingabeOben ? "klein" : "gross"} />
        <StatusPille laeuft={false} />
      </main>
    );
    zone = (
      <div className="daumenzone">
        {hostBuzzer}
        {/* "Loesung ansehen" nur im klassischen Modus: dort sitzen alle vor
            EINEM Geraet und der Host muss heimlich nachsehen koennen. Im
            Buzzer-Modus deckt der Gebuzzerte am eigenen Handy auf. */}
        {!isBuzzerHost && (
          <button
            type="button"
            className={`knopf neben halten${halten ? " gedrueckt" : ""}`}
            onPointerDown={(e) => {
              e.currentTarget.setPointerCapture(e.pointerId);
              setHalten(true);
              setShowSolution(true);
            }}
            onPointerUp={(e) => {
              e.currentTarget.releasePointerCapture(e.pointerId);
              setHalten(false);
              setShowSolution(false);
            }}
            onPointerCancel={() => {
              setHalten(false);
              setShowSolution(false);
            }}
            onContextMenu={(e) => e.preventDefault()}
          >
            <span className="zeile">
              <Icon name="auge" />
              Lösung ansehen
            </span>
            <small>gedrückt halten – nur du siehst sie</small>
          </button>
        )}
        <div className="reihe">
          <Button variant="neben" onClick={onNochmal}>
            <Icon name="nochmal" />
            Weiterhören
          </Button>
          <Button variant="haupt" onClick={onAufloesen}>
            Auflösen
            <Icon name="pfeil" />
          </Button>
        </div>
        {/* Nur klassisch: "keiner weiss es" ist dort etwas anderes als
            "jemand bekommt den Punkt". */}
        {!isBuzzerHost && (
          <Button variant="text" onClick={onSkip}>
            <Icon name="skip" />
            Song überspringen
          </Button>
        )}
      </div>
    );
  } else {
    // JUDGE
    const mehrere = taskType === "YEAR" && !jahrErgebnis;
    // Buzzer-Jahresrunde: der Raum rechnet — ausser er meldet sich nicht,
    // dann wie im Zuruf-Modus von Hand (jahrAuswertungAus)
    const jahrWirdAusgewertet = isBuzzerHost && taskType === "YEAR" && !jahrErgebnis && !jahrAuswertungAus;
    mitte = (
      <main className="spiel-mitte oben">
        <span className="ueberzeile gruen">Auflösung · Runde {roundCount}</span>
        <div className="loesung-karte">
          <SongLoesung isSongLoading={isSongLoading} songData={songData} />
        </div>
        {jahrErgebnis ? (
          <JahrUebersicht ergebnis={jahrErgebnis} meineId={meineId} farbe={farbe} spieler={raumSpieler} />
        ) : jahrWirdAusgewertet ? (
          // Buzzer-Modus: der Raum rechnet gleich — bis dahin kein
          // Kachel-Raster, das nach Handvergabe aussieht
          <p className="hinweis-text jahr-auswertung" role="status">
            Tipps werden ausgewertet <WartePunkte />
          </p>
        ) : (
          <>
            <div className="abschnitt-titel">
              <h2>{mehrere ? "Wer lag richtig?" : "Wer hatte recht?"}</h2>
              <span>{punktGegeben && !mehrere ? "Antippen bucht um" : mehrere ? "Mehrere möglich" : "Antippen"}</span>
            </div>
            <SpielerKacheln
              farbe={farbe}
              eintraege={players.map((p, i) => ({ key: i, name: name(p, i), score: p.score, an: hatPunkt(i) }))}
              onToggle={onVergebePunkt}
              keiner={{ onClick: onKeiner, an: keinerGewaehlt }}
            />
          </>
        )}
      </main>
    );
    zone = (
      <div className="daumenzone">
        <Button variant="haupt" onClick={onNaechsteRunde} disabled={jahrWirdAusgewertet}>
          Nächste Runde
          <Icon name="pfeil" />
        </Button>
        <Button variant="text" onClick={onWeiterhoeren}>
          <Icon name="play" />
          Song weiterhören
        </Button>
      </div>
    );
  }

  return (
    <div className={`spiel host-spiel${stimmung}`}>
      {kopf}
      {/* Hinweise unter dem Kopf: schieben den Inhalt, verdecken nichts */}
      {leisten}
      {mitte}
      {zone}
      {menue}
      {children}
    </div>
  );
}
